import { nextTick, watch } from 'vue';
import { createApp, type BootError } from './app';
import cookies from 'modernizr-esm/feature/cookies';
import { apiClient } from '@lib/http-client';
import { useGeneral } from './store/general';
import { init, addIntegration } from '@sentry/vue';
import { isStaleBundleError, staleBundleErrorMessages } from '@lib/captured-errors';
import { useUser } from '@/store/user';
import * as selectedBrandStorage from '@lib/selected-brand-storage';
import { intercomBoot, intercomUpdate } from '@lib/intercom';
import { watchForNewRelease } from '@lib/stale-release';
import { trackWindowScrollbar } from '@lib/window-scrollbar';
import { isOverlayNavigation } from '@lib/overlay-navigation';

// Brand selection survives across navigations / new tabs via three layered mechanisms:
//   1. sessionStorage — tab-scoped; survives reload (F5) so the same tab keeps its brand
//      even when localStorage has been overwritten by another tab.
//   2. localStorage   — origin-scoped; the only thing browsers reliably expose to a new
//      tab opened by middle/Ctrl/Cmd-click (sessionStorage is NOT copied for user-initiated
//      new-tab opens, and window.opener is null — this is intentional browser behavior).
//   3. The handler below — on link mousedown, the source tab writes its current brand to
//      localStorage so the soon-to-open new tab inherits *this* tab's brand rather than
//      whatever brand was switched-to most recently in some other tab.
document.addEventListener(
  'mousedown',
  (e) => {
    const anchor = e.target instanceof Element ? e.target.closest('a') : null;
    if (!anchor) return;
    const brandId = useUser().brandId;
    if (brandId) selectedBrandStorage.writeLocal(brandId);
  },
  true
);

// Ensure cookies are enabled and working - since we depend on them for lots of things (auth, etc)
let bootError: BootError | undefined;

function cookieError() {
  bootError = {
    title: 'Cookies disabled',
    message:
      'This website needs cookies enabled and working to function properly. Please enable cookies and then refresh the page.',
    fixed: true,
  };
}

if (!cookies) cookieError();

const url = window.location.href;
const host = window.location.hostname;
createApp({ bootError, url, host })
  .then(({ app, router, pinia }) => {
    // Full-screen loader (pages/frame/index.vue). Query-only changes (filter/season
    // writes to the URL) are excluded — pages show their own loading states for those.
    const nextFramePaint = () =>
      new Promise<void>((resolve) => {
        // rAF is throttled in hidden/occluded windows — cap the wait, those frames
        // aren't seen by anyone anyway.
        const timeout = setTimeout(resolve, 100);
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            clearTimeout(timeout);
            resolve();
          })
        );
      });

    // Registered before the guards below so a stale tab's navigation is turned into a full
    // page load before the loader is shown for a navigation that won't complete.
    watchForNewRelease(router);

    trackWindowScrollbar();

    let navigationId = 0;
    router.beforeEach(async (to, from) => {
      if (to.path === from.path) return;
      navigationId++;
      // A route can carry a param that opens a panel over the page it is already on — an article
      // flyout, say. The page underneath stays as it is and the panel brings its own skeleton, so
      // the loader would only cover the very thing the user is waiting to see.
      if (isOverlayNavigation(router, to, from)) return;
      useGeneral(pinia).loading = true;
      // Let the loader paint before the new page's render can block the main thread —
      // preloaded route chunks otherwise resolve within a single tick and it would
      // never become visible at all.
      await nextFramePaint();
    });

    router.afterEach((to, from, failure) => {
      const id = navigationId;
      if (!failure && to.path === from.path) return;
      // Hide only after the new page has rendered and painted, so a slow page render
      // stays covered by the loader. Guarded so a newer navigation keeps it visible.
      nextTick(async () => {
        await nextFramePaint();
        if (id === navigationId) useGeneral(pinia).loading = false;
      });
    });

    router.onError(() => {
      useGeneral(pinia).loading = false;
    });

    // Keep the tab title in sync with headTitle. A plain afterEach hook misses
    // titles set asynchronously by a route (e.g. once a product has loaded).
    watch(
      () => useGeneral(pinia).headTitle,
      (title) => (document.title = title),
      { immediate: true }
    );

    router.isReady().then(async () => {
      app.mount('#app');

      const appId = import.meta.env.VITE_INTERCOM_APP_ID;
      if (appId) {
        const intercomSettings: Parameters<typeof intercomBoot>[0] = { app_id: appId };

        const userStore = useUser(pinia);
        const user = userStore.current;
        if (user) {
          // Scope the Intercom identity to the active brand: a separate contact per
          // (user, brand) so conversations never bleed across brands' company cards.
          // currentBrandId is the PLM brand a switch actually changes (a switch full-
          // reloads, so this re-runs with the new value); bare user id when the user has
          // no PLM brand. Use the raw currentBrandId field — not the brandId getter,
          // which falls back to a company's brandID — so it matches the JWT's user_id
          // byte-for-byte (identity verification compares the two).
          const brandId = user.currentBrandId;
          intercomSettings.user_id = brandId != null ? `${user.id}:${brandId}` : user.id;
          intercomSettings.email = user.userName;
          intercomSettings.name = user.name;
          if (user.roles?.length) intercomSettings.role = user.roles.join(', ');

          // `company` resolves the currently-active brand (matches currentBrandId); the
          // stable company_id is what makes Intercom group users from the same customer
          // under one company card.
          const company = userStore.company;
          if (company) {
            intercomSettings.company = { company_id: String(company.id), name: company.name };
          }

          // Identity Verification: the backend signs the user's identity (JWT).
          // Falls back to an unsigned boot when no secret is configured — works
          // until enforcement is enabled on the Intercom side.
          try {
            const { jwt } = await apiClient.intercom_GetJwt();
            if (jwt) intercomSettings.intercom_user_jwt = jwt;
          } catch (e) {
            console.warn('Failed to fetch Intercom JWT, booting unsigned', e);
          }
        }
        intercomBoot(intercomSettings);
        router.afterEach(() => intercomUpdate());
      }
    });

    router.onError((error, to) => {
      if (isStaleBundleError(error)) {
        // A deploy might lead to changed hashes on the js bundle, and we don't keep old bundles around after deployment.
        // We silently reload the page to get the fresh bundles instead.
        // https://stackoverflow.com/a/74861436/4527934
        // https://vitejs.dev/guide/build#load-error-handling
        console.warn(`Tried navigating to ${to.fullPath} but couldn't load js/css bundle. Reloading page.`);
        window.location.href = to.fullPath;
      }
    });

    // Error tracking using Sentry. Only where a DSN is configured, which is production — errors
    // are suppressed from the console once Sentry is initialised, and that console is worth having
    // everywhere else.
    // See https://sentry.io/find-sourcing-ab/public-app/getting-started/javascript-vue/ and
    // https://github.com/vuejs/vue/issues/8433 for details.
    if (import.meta.env.VITE_SENTRY_DSN) {
      init({
        app,
        dsn: import.meta.env.VITE_SENTRY_DSN,
        release: import.meta.env.VITE_RELEASE_TAG,

        denyUrls: [/intercom\.io/, /intercomcdn\.com/],

        // Stale bundle errors after deployments — handled by router.onError with a page reload
        ignoreErrors: staleBundleErrorMessages,

        replaysSessionSampleRate: 0.01,
        replaysOnErrorSampleRate: 1.0,

        initialScope: {
          user: {
            id: useUser().current!.id,
            brand: useUser().company!.name,
            email: useUser().current!.userName,
          },
        },
      });

      // Lazy-load the replay integration to keep it out of the main bundle
      import('@sentry/vue')
        .then(({ replayIntegration }) => {
          addIntegration(
            replayIntegration({
              maskAllText: false,
              blockAllMedia: false,
              maskAllInputs: false,
              networkDetailAllowUrls: ['/api'],
            })
          );
        })
        .catch(() => {});
    }
  })
  .catch((err) => {
    // app.js throws { redirect } synchronously for users logged in as a non-PLM
    // brand. Previously the SSR server caught this and sent an HTTP 302; with no
    // server we perform the redirect client-side.
    if (err && err.redirect) return window.location.replace(err.redirect);
    throw err;
  });
